import React, { createContext, useContext } from 'react';
import { useDownloadEvents } from '@gov-nx/core/events';
import {
	useObjectState,
	useRezaSwitcher,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import { toStringDate } from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { ServiceContextTypes, UsageModalState } from './service.types';
import type { FilterForm } from './useForm';
import { useForm } from './useForm';
import { SERVICE_NAME } from './constants';
import { usePoMutation } from '@gov-nx/api';

export const ServiceContext = createContext<ServiceContextTypes | null>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function ZaznamOVyuzivaniUdajuZIsssContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const [usageModal, setUsageFormData] = useObjectState<UsageModalState>({
		open: false,
	});

	const { downloadIsss } = useDownloadEvents();

	const [downloadControl, setDownloadControl] = useObjectState({
		isDownloading: false,
	});

	const rezaSwitcher = useRezaSwitcher({ code });

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(SERVICE_NAME);

	const formDefinition = useForm({
		code,
		isDownloading: downloadControl.isDownloading,
		isLowAuthorization,
	});

	const downloadMutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: (data: FilterForm) =>
			new Promise((resolve) => {
				downloadIsss({
					datumOd: data.datumOd ? toStringDate(data.datumOd) : '',
					datumDo: data.datumDo ? toStringDate(data.datumDo) : '',
					ais: Array.isArray(data.ais) ? data.ais : undefined,
					callback: () => {
						resolve(null);
						setDownloadControl({
							isDownloading: false,
						});
					},
				});
			}),
	});

	const handleSubmit = async (data: FilterForm) => {
		setDownloadControl({
			isDownloading: true,
		});
		await downloadMutation.mutateAsync(data);
	};

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	const handleShowModal = (data: FilterForm) => {
		setUsageFormData({
			open: true,
			formData: data,
		});
	};

	const onShowUsageClick =
		formDefinition.formMethods.handleSubmit(handleShowModal);

	const closeUsageModal = () => {
		setUsageFormData({ open: false });
	};

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				rezaSwitcher,
				onShowUsageClick,
				closeUsageModal,
				isDownloading: downloadControl.isDownloading,
				onSubmit,
				usageModal,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useZaznamOVyuzivaniUdajuZIsssContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
